import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";

interface EasySessionContextType {
  isEasySessionInit: boolean;
  isLoggedIntoEasy: boolean;
  setIsLoggedIntoEasy: (value: boolean) => void;
  preferredName: string;
  checkLogin: () => Promise<void>;
}

const EasySessionContext = createContext<EasySessionContextType | undefined>(
  undefined,
);

interface EasySessionLoadingContextType {
  isFetchingEasyLoginStatus: boolean;
}

const EasySessionLoadingContext = createContext<
  EasySessionLoadingContextType | undefined
>(undefined);

interface EasySessionProviderProps {
  children: ReactNode;
}

export const EasySessionProvider: React.FC<EasySessionProviderProps> = ({
  children,
}) => {
  const [isLoggedIntoEasy, setIsLoggedIntoEasy] = useState<boolean>(false);
  const [isEasySessionInit, setIsEasySessionInit] = useState<boolean>(true);
  const [isFetchingEasyLoginStatus, setIsFetchingEasyLoginStatus] =
    useState<boolean>(false);
  const [preferredName, setPreferredName] = useState<string>("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  /*  const { data: easyLogin, refetch: refetchEasyLoginState } =
    useGetApiEasyCheckLoginState(); */
  const checkLogin = useCallback(async (): Promise<void> => {
    console.log("checkLogin");

    setIsFetchingEasyLoginStatus(true);
    try {
      const response = await fetch("/api/Easy/CheckLoginState", {
        method: "GET",
      });

      if (response.status === 200) {
        const data = await response.json();
        if (typeof data?.string === "string" && data.string !== preferredName) {
          setPreferredName(data.string);
        }
        if (!isLoggedIntoEasy) {
          setIsLoggedIntoEasy(true);
        }
      } else if (response.status === 401) {
        if (isLoggedIntoEasy) {
          setIsLoggedIntoEasy(false);
        }
      } else {
        const error = await response.text();
        console.warn(error);
        setIsLoggedIntoEasy(false); //TODO insert generic Error handler
      }
    } catch (error) {
      console.warn(error);
      if (isLoggedIntoEasy) {
        setIsLoggedIntoEasy(false);
      }
    } finally {
      if (isEasySessionInit) {
        console.log("finalize easy init");
        setIsEasySessionInit(false);
        // setTimeout(() => setIsEasySessionInit(false), 5000); //TODO Remove
      }
      setIsFetchingEasyLoginStatus(false);
    }
  }, [isEasySessionInit, isLoggedIntoEasy, preferredName]);
  const checkLoginEffectEvent = useEffectEvent(() => {
    checkLogin();
  });

  useEffect(() => {
    console.log("easy session provider effect");
    // eslint-disable-next-line react-hooks/set-state-in-effect
    checkLoginEffectEvent();
    intervalRef.current = setInterval(async () => {
      console.log("interval", typeof checkLoginEffectEvent);

      checkLoginEffectEvent();
    }, 60000); //previously 300000

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const loadingValue = useMemo(
    () => ({ isFetchingEasyLoginStatus }),
    [isFetchingEasyLoginStatus],
  );

  const sessionValue = useMemo(
    () => ({
      isEasySessionInit,
      isLoggedIntoEasy,
      setIsLoggedIntoEasy,
      preferredName,
      checkLogin,
    }),
    [checkLogin, isEasySessionInit, isLoggedIntoEasy, preferredName],
  );

  return (
    <EasySessionLoadingContext.Provider value={loadingValue}>
      <EasySessionContext.Provider value={sessionValue}>
        {children}
      </EasySessionContext.Provider>
    </EasySessionLoadingContext.Provider>
  );
};

export const useEasySession = (): EasySessionContextType => {
  const context = useContext(EasySessionContext);
  if (!context) {
    throw new Error("useEasySession must be used within a EasySessionProvider");
  }
  return context;
};

export const useEasySessionLoading = (): EasySessionLoadingContextType => {
  const context = useContext(EasySessionLoadingContext);
  if (!context) {
    throw new Error(
      "useEasySessionLoading must be used within a EasySessionProvider",
    );
  }
  return context;
};
